import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { Geolocation } from '@capacitor/geolocation'
import { LocalNotifications } from '@capacitor/local-notifications'

interface LocationState {
  latitude: number
  longitude: number
  accuracy?: number
  heading?: number
  speed?: number
}

export interface GeolocationHandle {
  readonly location: LocationState | null
  readonly permissionGranted: boolean
  readonly error: string | null
  requestLocation: () => Promise<boolean>
}

export function geolocationDisabledForTrainLodBenchmark(
  env: Record<string, string | boolean | undefined> = import.meta.env,
): boolean {
  return env.VITE_TRAIN_LOD_BENCHMARK === 'true'
}

export function useGeolocation(): GeolocationHandle {
  // The benchmark runner supplies a deterministic synthetic fleet/camera and never
  // consumes device location. Requesting it would put a native permission surface over
  // the renderer and invalidate both visual evidence and frame timing.
  const benchmarkDisabled = geolocationDisabledForTrainLodBenchmark()
  let location = $state<LocationState | null>(null)
  let permissionGranted = $state(false)
  let error = $state<string | null>(null)

  // Plain locals: these coordinate async work, nothing renders from them.
  let watchId: string | null = null
  let mounted = true
  // A watch is expensive; guard against starting a second one when requestLocation()
  // runs again (e.g. the user grants access from Map Options after first denying).
  let starting = false

  async function startWatch() {
    if (watchId || starting) return
    starting = true
    try {
      watchId = await Geolocation.watchPosition(
        {
          enableHighAccuracy: true,
          timeout: 10_000,
          maximumAge: 3_000,
        },
        (pos, err) => {
          if (!mounted) return
          if (err) {
            error = err.message
            // Browser (or OS) permission denial surfaces here as code 1, so reflect it
            // so location affordances hide instead of advertising a disabled feature.
            if ((err as { code?: number }).code === 1) permissionGranted = false
            return
          }
          if (pos) {
            // A successful fix implies permission; covers web, where there is no
            // explicit grant step above.
            permissionGranted = true
            error = null
            location = {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
              heading: pos.coords.heading ?? undefined,
              speed: pos.coords.speed ?? undefined,
            }
          }
        }
      )
    } catch (e) {
      if (mounted) error = (e as Error).message
    } finally {
      starting = false
    }
  }

  // Request location access and, if granted, begin watching. Safe to call repeatedly:
  // it's the single entry point used both on mount and by the "Enable location" control
  // in Map Options. Returns whether access ended up granted.
  //
  // On native, requestPermissions() shows the OS dialog only the first time — once the
  // user has permanently denied, it returns 'denied' silently (no dialog), which is the
  // signal Map Options uses to deep-link to system settings instead.
  async function requestLocation(): Promise<boolean> {
    if (benchmarkDisabled) return false
    try {
      // requestPermissions() triggers the OS dialog and reports the grant state on
      // native, but is *unimplemented on web* (it throws). In a browser the permission
      // prompt fires on the first watchPosition() call instead, so we skip the explicit
      // request there and derive `permissionGranted` from whether a fix actually arrives
      // (or a code-1 error, meaning the user denied it).
      if (Capacitor.isNativePlatform()) {
        const perm = await Geolocation.requestPermissions()
        const granted = perm.location === 'granted'
        if (!mounted) return granted
        permissionGranted = granted

        if (!granted) {
          error = 'Location permission denied'
          return false
        }

        try {
          await LocalNotifications.requestPermissions()
        } catch {
          // non-blocking; notifications are optional
        }
      }

      await startWatch()
      return true
    } catch (e) {
      if (mounted) error = (e as Error).message
      return false
    }
  }

  $effect(() => {
    mounted = true
    if (benchmarkDisabled) {
      return () => { mounted = false }
    }
    void requestLocation()

    // When the user returns from the OS settings screen (having toggled location on),
    // the app resumes — re-check the permission and start watching so tracking begins
    // without a manual retry or app restart. Cheap: checkPermissions() never prompts,
    // and startWatch() no-ops if a watch is already running.
    let resumeHandle: { remove: () => void } | null = null
    if (Capacitor.isNativePlatform()) {
      void App.addListener('resume', async () => {
        try {
          const perm = await Geolocation.checkPermissions()
          if (!mounted) return
          if (perm.location === 'granted') {
            permissionGranted = true
            await startWatch()
          }
        } catch {
          // checkPermissions can throw on some platforms; ignore and keep current state.
        }
      }).then((handle) => {
        // The listener may resolve after teardown; drop it immediately if so.
        if (mounted) resumeHandle = handle
        else handle.remove()
      })
    }

    return () => {
      mounted = false
      resumeHandle?.remove()
      if (watchId) {
        void Geolocation.clearWatch({ id: watchId })
        watchId = null
      }
    }
  })

  return {
    get location() { return location },
    get permissionGranted() { return permissionGranted },
    get error() { return error },
    requestLocation,
  }
}
